/**
 * Opening a chest.
 *
 * A chest a child has earned opens **now** — no timer, no key, nothing to buy
 * ([Economy](../../../docs/ECONOMY.md#the-rules-that-keep-it-fair)). So this is
 * one tap and what was inside, and the only state it has is whether that tap
 * has happened yet.
 *
 * The roll is the server's ([D-086](../../../docs/DECISIONS.md#d-086)): what a
 * chest contains cannot be decided by a client a child could edit, and the
 * no-duplicates rule needs the shelf as the database knows it.
 */

import { useState } from 'react';
import { type Client, today } from './api.js';
import { say } from './audio.js';
import ChestOdds from './ChestOdds.js';

/** What a child sees before they tap. */
export interface Waiting {
  readonly id: string;
  readonly tier: string;
  readonly source: string;
  /**
   * This chest's odds and the bad-luck rules, in words, from the server's own
   * numbers ([D-151](../../../docs/DECISIONS.md#d-151)). Optional, because a
   * server a deploy behind does not send them, and then nothing is said
   * rather than something made up here.
   */
  readonly odds?: string;
  readonly luck?: string;
  /** What it may hold for a pet. Optional for the same reason as the odds. */
  readonly treats?: string;
}

interface Opened {
  readonly acorns: number;
  readonly item: { readonly id: string; readonly rarity: string } | null;
  readonly viaPity: boolean;
  /** The band the bad-luck rules promised, when they are why it is that good. */
  readonly luck?: string | null;
  readonly catalogueComplete: boolean;
  /** Food for a pet. Optional: a server a deploy behind sends none. */
  readonly food?: { readonly id: string; readonly qty: number } | null;
  /** An egg, when the chest carried one. */
  readonly egg?: string | null;
}

/** Each food as a child sees it. Matches `FOOD` in core. */
const FOODS: Readonly<
  Record<string, { readonly name: string; readonly emoji: string }>
> = {
  berries: { name: 'berries', emoji: '🍓' },
  nuts: { name: 'nuts', emoji: '🌰' },
  honeycomb: { name: 'honeycomb', emoji: '🍯' },
  starfruit: { name: 'star fruit', emoji: '⭐' },
};

/** What the egg is called on the chest. Matches `EGGS` in core. */
const EGG_NAMES: Readonly<Record<string, string>> = {
  speckled: 'a speckled egg',
  striped: 'a striped egg',
  shining: 'a shining egg',
};

/** The headline, from the best thing inside. */
const headline = (opened: Opened): string => {
  if (opened.egg != null) return 'An egg!';
  if (opened.item !== null) return 'Something to wear!';
  if (opened.food != null) return 'A treat for your pet!';
  return 'Acorns!';
};

/**
 * When bad luck ended, say so: the rule is only a comfort if a child can see
 * it working. Never said when the dice did it by themselves.
 */
const luckLine = (opened: Opened): string | null => {
  if (opened.luck === 'epic') return 'No more bad luck: this one had to be epic!';
  if (opened.luck === 'rare') return 'No more bad luck: this one had to be rare!';
  if (opened.viaPity) return 'No more bad luck: this one had to have something!';
  return null;
};

/** One per tier, so a gold chest does not look like a wooden one. */
const LOOKS: Readonly<Record<string, string>> = {
  wooden: '🪵',
  bronze: '🥉',
  silver: '🥈',
  gold: '🥇',
  rainbow: '🌈',
};

/** Why it turned up, in a child's words rather than the column's. */
const BECAUSE: Readonly<Record<string, string>> = {
  lesson: 'for three lessons',
  quests: 'for all three quests',
  chapter: 'for finishing a chapter',
  streak: 'for your streak',
  // The three best chests in the game say what they were for, because that is
  // most of the reward ([Economy](../../../docs/ECONOMY.md#chests)). "A chest"
  // with no reason is the same chest as any other.
  perfect: 'for a perfect chapter',
  book: 'for finishing a book',
  combo: 'for twenty in a row',
  milestone: 'for a Long Path milestone',
  story: 'for finishing a story',
  mastered: 'for mastering a chapter',
  // Framed the way the practice screen is: tricky ones beaten, never
  // mistakes cleared (D-161).
  practice: 'for beating all the tricky ones',
};

export default function Chest({
  chest,
  client,
  onDone,
}: {
  readonly chest: Waiting;
  readonly client: Client;
  readonly onDone: () => void;
}) {
  const [opened, setOpened] = useState<Opened | null>(null);
  const [opening, setOpening] = useState(false);
  const [about, setAbout] = useState(false);

  const open = () => {
    // The guard is a ref-shaped problem solved with state here only because a
    // second tap cannot arrive within one frame: the button is replaced by its
    // own pending label. The server refuses a double open regardless (D-083).
    if (opening) return;
    setOpening(true);
    Promise.resolve()
      .then(() => client.chest.open.mutate({ chestId: chest.id, day: today() }))
      .then((result) => {
        // Null is a second tap that lost the race, or a chest already opened on
        // another device. Nothing went wrong, so nothing is said about it.
        if (result != null) {
          setOpened(result);
          // There is no recording for this, so `say` falls through to the
          // device's own voice — which is the normal path everywhere (D-064).
          void say(null, headline(result), { language: 'en' });
        } else {
          onDone();
        }
      })
      .catch(() => {
        // A chest that will not open is not worth trapping a child on. It stays
        // unopened on the server, so it will be waiting next time.
        onDone();
      });
  };

  if (about) {
    return (
      <ChestOdds
        client={client}
        tier={chest.tier}
        onClose={() => {
          setAbout(false);
        }}
      />
    );
  }

  if (opened !== null) {
    return (
      <div className="card" style={{ marginTop: 12, width: '100%' }}>
        <h3>
          <span aria-hidden="true">{LOOKS[chest.tier] ?? '🎁'}</span> {headline(opened)}
        </h3>
        <p className="tiny" style={{ marginTop: 4 }}>
          <span aria-hidden="true">🌰</span> {opened.acorns} acorns
          {opened.item !== null && (
            <> and a {opened.item.rarity} thing for your animal</>
          )}
        </p>
        {opened.food != null && (
          <p className="tiny" style={{ marginTop: 4 }}>
            <span aria-hidden="true">{FOODS[opened.food.id]?.emoji ?? '🍽️'}</span>{' '}
            {opened.food.qty} × {FOODS[opened.food.id]?.name ?? opened.food.id} for your
            pet
          </p>
        )}
        {opened.egg != null && (
          <p className="tiny" style={{ marginTop: 4 }}>
            <span aria-hidden="true">🥚</span> {EGG_NAMES[opened.egg] ?? 'an egg'}! It
            hatches as you do lessons — look in Pets.
          </p>
        )}
        {luckLine(opened) !== null && (
          <p className="tiny" style={{ marginTop: 4 }}>
            {luckLine(opened)}
          </p>
        )}
        {opened.catalogueComplete && (
          // Worth saying out loud: it looks like a plain acorn chest otherwise,
          // and "you own everything" is the opposite of a disappointment.
          <p className="tiny" style={{ marginTop: 4 }}>
            You already have everything! So it is all acorns.
          </p>
        )}
        <button
          className="btn"
          type="button"
          style={{ marginTop: 10 }}
          onClick={onDone}
        >
          Nice
        </button>
      </div>
    );
  }

  return (
    <div className="card" style={{ marginTop: 12, width: '100%' }}>
      <h3>
        {/*
          A source this app has no words for — a server one deploy ahead
          ([D-066](../../../docs/DECISIONS.md#d-066)) — still reads as a
          sentence rather than as "A chest" with a space after it.
        */}
        <span aria-hidden="true">{LOOKS[chest.tier] ?? '🎁'}</span>{' '}
        {`A chest ${BECAUSE[chest.source] ?? ''}`.trim()}
      </h3>
      {/* The odds, where the chest is: D-021's promise, kept on the spot. */}
      {chest.odds !== undefined && (
        <p className="tiny" style={{ marginTop: 4 }}>
          {chest.odds}
        </p>
      )}
      {chest.treats !== undefined && (
        <p className="tiny" style={{ marginTop: 4 }}>
          {chest.treats}
        </p>
      )}
      {chest.luck !== undefined && (
        <p className="tiny" style={{ marginTop: 4 }}>
          {chest.luck}
        </p>
      )}
      <button
        className="chip"
        type="button"
        style={{ marginTop: 8 }}
        onClick={() => {
          setAbout(true);
        }}
      >
        How do chests work?
      </button>
      <button
        className="btn"
        type="button"
        style={{ marginTop: 10 }}
        disabled={opening}
        onClick={open}
      >
        {opening ? 'Opening…' : 'Open it'}
      </button>
    </div>
  );
}
